<template>
  <div class="mr-3">
    <div class="card">
      <div class="card-body">
        <!-- Tabs -->
        <a-tabs
          v-model="activeTab"
          @change="handleTabChange"
          v-if="activeTab && !isLoading"
        >
          <a-tab-pane
            key="stats"
            :tab="$t('recette.stat')"
            v-if="verifyRoleAccess('stat_tab')"
          >
            <stats-tab :key="`stats-tab-${tabKey}`" />
          </a-tab-pane>

          <a-tab-pane
            key="cheques"
            :tab="$t('recette.chequeTraite')"
            v-if="verifyRoleAccess('cheque_traite_tab')"
          >
            <cheques-tab
              :key="`cheques-tab-${tabKey}`"
              ref="chequesTab"
              :settings="settings"
              @openEditModal="openEditModal"
            />
          </a-tab-pane>

          <a-tab-pane
            key="bordereaux"
            :tab="$t('recette.borderaux')"
            v-if="verifyRoleAccess('borderaux_tab')"
          >
            <bordereaux-tab
              :key="`bordereaux-tab-${tabKey}`"
              :settings="settings"
              :user="user"
            />
          </a-tab-pane>
        </a-tabs>

        <!-- Show loading state -->
        <div v-else-if="isLoading" class="text-center p-4">
          <a-spin size="large" />
          <div class="mt-2">{{ $t("tresorerie.verificationPermissions") }}</div>
        </div>

        <!-- Show message if no tabs are accessible -->
        <div v-else class="text-center p-4">
          <a-empty :description="$t('tresorerie.aucunAcces')" />
        </div>
      </div>
    </div>

    <edit-cheque-modal
      :visible="editModalVisible"
      :chequeData="selectedCheque"
      @close="handleEditModalClose"
      @updated="handleChequeUpdated"
    />
  </div>
</template>

<script>
import { mapState } from "vuex";
import EditChequeModal from "./cheques/editChequeModal.vue";
import ChequesTab from "./cheques/ChequesTab.vue";
import BordereauxTab from "./bordereaux/BordereauxTab.vue";
import StatsTab from "./stats/StatsTab.vue";

export default {
  name: "cheques",
  computed: {
    ...mapState(["settings", "user"]),
    // Dynamically determine the first accessible tab
    firstAccessibleTab() {
      const tabs = [
        { key: "stats", access: "stat_tab" },
        { key: "cheques", access: "cheque_traite_tab" },
        { key: "bordereaux", access: "borderaux_tab" },
      ];

      for (const tab of tabs) {
        const hasAccess = this.verifyRoleAccess(tab.access);

        if (hasAccess) {
          return tab.key;
        }
      }

      console.warn("No accessible tabs found");
      return null; // No accessible tabs
    },
  },
  mounted() {
    // Set the first accessible tab as active when component mounts

    if (this.firstAccessibleTab) {
      this.activeTab = this.firstAccessibleTab;
    } else {
      console.warn("No accessible tabs found for user");
    }
    this.isLoading = false;
  },
  watch: {
    // Watch for changes in user access and update active tab if needed
    "user.access": {
      handler() {
        if (
          this.firstAccessibleTab &&
          this.activeTab !== this.firstAccessibleTab
        ) {
          // If current active tab is no longer accessible, switch to first accessible tab
          if (!this.verifyTabAccess(this.activeTab)) {
            this.activeTab = this.firstAccessibleTab;
          }
        }
      },
      deep: true,
    },
  },
  components: {
    EditChequeModal,
    ChequesTab,
    BordereauxTab,
    StatsTab,
  },
  data() {
    return {
      activeTab: null, // Will be set dynamically based on user access
      tabKey: 0, // For forcing component reload
      editModalVisible: false,
      selectedCheque: {},
      isLoading: true, // Loading state while determining access
    };
  },
  methods: {
    verifyRoleAccess: function (accessRoles) {
      const existing = this.user.access;
      if (existing.all) return true;
      if (
        existing.tresorerie === true ||
        (existing.tresorerie &&
          existing.tresorerie.length > 0 &&
          existing.tresorerie.find((e) => e === accessRoles))
      )
        return true;
      else return false;
    },
    // Helper method to verify if a specific tab is accessible
    verifyTabAccess(tabKey) {
      const tabAccessMap = {
        stats: "stat_tab",
        cheques: "cheque_traite_tab",
        bordereaux: "borderaux_tab",
      };

      const requiredAccess = tabAccessMap[tabKey];
      return requiredAccess ? this.verifyRoleAccess(requiredAccess) : false;
    },
    // Tab management with component reloading
    handleTabChange(activeKey) {
      // Verify the user has access to the selected tab
      if (this.verifyTabAccess(activeKey)) {
        this.activeTab = activeKey;
        // Force component reload by changing the key
        this.tabKey++;
      } else {
        // If user doesn't have access, revert to current tab and show warning
        this.$message.warning(this.$t("tresorerie.pasAccesOnglet"));
        // Force the activeTab back to the current accessible tab
        this.$nextTick(() => {
          this.activeTab = this.activeTab;
        });
      }
    },

    openEditModal(record) {
      this.selectedCheque = { ...record };
      this.editModalVisible = true;
    },

    handleEditModalClose() {
      this.editModalVisible = false;
      this.selectedCheque = {};
    },

    handleChequeUpdated(updatedCheque) {
      this.editModalVisible = false;
      this.selectedCheque = {};
      // Refresh the cheques tab data
      this.$refs.chequesTab?.fetchData();
    },
  },
};
</script>

<style>
@import "../../common.css";
</style>
